import { LoadingButton } from '@mui/lab';
import { Alert, Dialog, DialogActions, DialogContent, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { Months } from '../../../../Component/Model/Enum/BEE123Enums';
import { formatCurrencyInputValue } from '../../../../Helpers/CurrencyFormatter';
import { isNullOrEmpty } from '../../../../Component/Model/helpers';
import { PlatformSalary } from '../../../../Component/Model/Dto/Platform/PlatformSalary';
import { PlatformSalaryService } from '../../../../Component/Api/PlatformSalary/PlatformSalaryService';
import { SkeletonLoading } from '../../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../../State/user.Atom';
import { PlatformPeopleSalaryEditRole, UserAdminRole } from '../../../../Component/Common/Constants/UserRoles';
import { ValidatedMobileDatePicker } from '../../../../Component/Common/Components/ValidatedMobileDatePicker/ValidatedMobileDatePicker';

interface IProps {
    personId: string;
    selectedYearForEdit?: number;
    handleClose: () => void;
    showModal: boolean;
}

export const PlatformSalaryModal = (props: IProps) => {
    const currentYear = new Date().getFullYear();
    const userRoles = useRecoilValue(userRolesState);
    const [salaryDataHasError, setSalaryDataHasError] = useState(false);
    const [salaryData, setSalaryData] = useState<PlatformSalary.PlatformSalaryDataDto[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(props.selectedYearForEdit ? props.selectedYearForEdit : currentYear);

    const editRoleAccess = [
        PlatformPeopleSalaryEditRole,
        UserAdminRole
    ]

    const isEditable = userRoles.some(r => editRoleAccess.includes(r));

    const handleSave = async () => {
        setIsSaving(true);
        try {
            let hasError = false;
            const salaryDataForSubmit: PlatformSalary.PlatformSalaryDataDto[] = salaryData.map((item) => {
                const ctcString = item.ctc?.toString();
                const numericValue = ctcString?.replace(/[^0-9.,]/g, '')?.replace(',', '.');
                const parsedCtc = isNullOrEmpty(numericValue) ? null : parseFloat(numericValue!);
                if (parsedCtc != null) {
                    if (isNaN(parsedCtc!)) {
                        hasError = true;
                    }
                }

                return {
                    id: item.id,
                    monthId: item.monthId,
                    ctc: parsedCtc,
                };
            });

            if (hasError) {
                setSalaryDataHasError(true);
                return;
            } else {
                setSalaryDataHasError(false);
            }

            if (props.selectedYearForEdit) {
                const request = {
                    personId: props.personId,
                    year: props.selectedYearForEdit,
                    data: salaryDataForSubmit
                } as PlatformSalary.PlatformSalaryDto;

                await PlatformSalaryService.updateSalaryForYear(request);

            } else {
                const request = {
                    personId: props.personId,
                    year: selectedYear,
                    data: salaryDataForSubmit
                } as PlatformSalary.PlatformSalaryDto;

                await PlatformSalaryService.createSalaryForYear(request);
            }
            toast.success(`Successfully saved salary for ${selectedYear}`);
            props.handleClose();
        } catch (error: any) {
            toast.error(error.response.data);
        } finally {
            setIsSaving(false);
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            if (!props.selectedYearForEdit) {
                const initialData = Array.from({ length: 12 }, (_, i) => ({
                    id: i + 1,
                    ctc: null,
                    monthId: i + 1,
                }));
                setSalaryData(initialData);
            } else {
                const response = await PlatformSalaryService.getPersonSalariesForYear(props.personId, props.selectedYearForEdit);
                setSalaryData(response.data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleCtcChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowId: number) => {
        if (!isNullOrEmpty(event.target.value)) {
            const numericValue = event.target.value.replace(/[^0-9.,\,,\.,\s]/g, '');
            const formattedValue = formatCurrencyInputValue(numericValue, 2);

            const updatedData = salaryData.map((item) =>
                item.id === rowId ? { ...item, ctc: formattedValue } : item
            );
            setSalaryData(updatedData);
        } else {
            const updatedData = salaryData.map((item) =>
                item.id === rowId ? { ...item, ctc: null } : item
            );
            setSalaryData(updatedData);
        }
    }

    useEffect(() => {
        fetchData();
    }, [props.selectedYearForEdit, props.personId]);

    const titleMessage = isEditable ? `Edit Salary for ${props.selectedYearForEdit}` : `View Salary for ${props.selectedYearForEdit}`;

    return (
        <Dialog open={props.showModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
            <CustomDialogTitle onClose={props.handleClose}>
                {props.selectedYearForEdit ? titleMessage : "Capture Salary Year"}
            </CustomDialogTitle>
            <DialogContent>
                {isLoading ?
                    <SkeletonLoading formLoading FieldAmount={6} width="100%" />
                    :
                    <Grid container spacing={2}>
                        {!props.selectedYearForEdit &&
                            <Grid item xs={4}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <ValidatedMobileDatePicker
                                        value={new Date(selectedYear, 0, 1)}
                                        views={['year']}
                                        inputFormat="yyyy"
                                        label="Year"
                                        onChange={(date: Date) => {
                                            if (date != null) {
                                                setSelectedYear(date.getFullYear());
                                            }
                                        }}
                                        renderInput={(params: any) => <TextField {...params} fullWidth variant='outlined' size='small' required sx={{ mt: 1 }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        }

                        {salaryDataHasError && <Grid item xs={12}><Alert severity="error">Invalid salary values detected. Please ensure all values are valid numbers.</Alert></Grid>}

                        <Grid item xs={12}>
                            <table style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: "var(--semantics-disabled, #4d4f53)", height: '40px' }}>
                                        <th><Typography variant='body2' fontWeight="700" ml={'7px'} color='#FFF'>Month</Typography></th>
                                        <th><Typography variant='body2' fontWeight="700" ml={'7px'} color='#FFF'>CTC Salary</Typography></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salaryData.map((row) => (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #C9C9C980', height: '40px' }}>
                                            <td><Typography variant='body2' ml={'7px'}> {Months[row.monthId]} </Typography></td>
                                            <td>
                                                <TextField
                                                    label={" "}
                                                    sx={{
                                                        padding: '3px',
                                                        '& .MuiInputLabel-shrink': {
                                                            transform: 'none',
                                                        },
                                                    }}
                                                    value={formatCurrencyInputValue(row.ctc, 2)}
                                                    onChange={(e) => {
                                                        handleCtcChanged(e, row.id);
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R</InputAdornment>,
                                                    }}
                                                    InputLabelProps={{
                                                        shrink: false
                                                    }}
                                                    disabled={!isEditable}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Grid>
                    </Grid>
                }
            </DialogContent>
            <DialogActions>
                {isEditable &&
                    <>
                        <LoadingButton color='primary' variant='contained' onClick={handleSave} loading={isSaving}>
                            Save
                        </LoadingButton>
                        <MDButton color='primary' variant='outlined' onClick={props.handleClose}>
                            Cancel
                        </MDButton>
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}
